import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Package } from 'lucide-react';
import { useStore } from 'context/StoreContext';
import { useApi } from 'hooks/useApi';
import { useDebounce } from 'hooks/useDebounce';
import { ProductService } from 'services/catalog/product.service';
import { Table, Column } from 'components/common/Table';
import { LoadingScreen, EmptyState } from 'components/common/Spinner';
import { StoreProduct } from 'dtos/catalog/product.dto';
import { formatCurrency, formatPercent } from 'utils/formatters';
import { Pagination } from 'components/common/Pagination';

const PAGE_SIZE = 8;

export default function ProductsPage() {
  const { org, currentStore } = useStore();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);

  const { data: categories } = useApi(
    () => ProductService.getCategories(org?.id ?? '', currentStore?.id ?? ''),
    [org?.id, currentStore?.id]
  );

  const { data: products, loading } = useApi(
    () => ProductService.list(org?.id ?? '', currentStore?.id ?? '', debouncedSearch || undefined, category || undefined, page, PAGE_SIZE),
    [currentStore?.id, debouncedSearch, category, page]
  );

  const columns: Column<StoreProduct>[] = [
    { key: 'name', header: 'Product', sortable: true, render: sp => (
      <div>
        <p className="text-sm font-medium text-gray-900">{sp.product?.product_name}</p>
        <p className="text-xs text-gray-400">{sp.product?.upc} · {sp.product?.brand}</p>
      </div>
    )},
    { key: 'category', header: 'Category', render: sp => (
      <span className="text-sm text-gray-600">{sp.product?.category ?? '—'}</span>
    )},
    { key: 'inventory', header: 'Inventory', align: 'right', sortable: true, render: sp => (
      <span className="text-sm font-mono">{(sp.quantity_on_hand ?? 0).toLocaleString()}</span>
    )},
    { key: 'cost', header: 'Cost', align: 'right', sortable: true, render: sp => (
      <span className="text-sm font-mono">{sp.current_cost === null || sp.current_cost === undefined ? '—' : formatCurrency(sp.current_cost, 2)}</span>
    )},
    { key: 'retail', header: 'Retail', align: 'right', sortable: true, render: sp => (
      <span className="text-sm font-mono">{formatCurrency(sp.current_retail_price, 2)}</span>
    )},
    { key: 'margin', header: 'Margin', align: 'right', sortable: true, render: sp => {
      const margin = sp.gross_margin;
      if (margin === null || margin === undefined) return <span className="text-sm font-medium text-gray-400">—</span>;
      const color = margin < 0.20 ? 'text-red-600' : margin < 0.30 ? 'text-orange-600' : 'text-green-700';
      return <span className={`text-sm font-medium ${color}`}>{formatPercent(margin)}</span>;
    }},
    { key: 'units', header: 'Units/30d', align: 'right', render: sp => (
      <span className="text-sm text-gray-600">{sp.units_last_30d?.toLocaleString() ?? '—'}</span>
    )},
    { key: 'sales', header: 'Sales/30d', align: 'right', render: sp => (
      <span className="text-sm text-gray-600">{sp.net_sales_last_30d ? formatCurrency(sp.net_sales_last_30d) : '—'}</span>
    )},
  ];

  if (loading && !products) return <LoadingScreen />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package size={20} className="text-gray-600" />
          <h1 className="text-xl font-bold text-gray-900">Products</h1>
          <span className="text-sm text-gray-400">({products?.meta.total ?? 0})</span>
        </div>
      </div>

      {/* Search + filter */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" placeholder="Search products, UPC, brand…"
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="relative">
          <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <select
            value={category} onChange={e => { setCategory(e.target.value); setPage(1); }}
            className="pl-9 pr-8 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
          >
            <option value="">All categories</option>
            {(categories ?? []).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {!products?.data.length ? (
          <EmptyState icon={<Package size={40} />} title="No products found" message="Try adjusting your search or filter." />
        ) : (
          <Table
            columns={columns}
            data={products.data}
            keyExtractor={sp => sp.id}
            onRowClick={sp => navigate(`/products/${sp.product_id}`)}
          />
        )}
        {products && <Pagination page={page} pageSize={PAGE_SIZE} total={products.meta.total} onPageChange={setPage} />}
      </div>
    </div>
  );
}

